import type { Strings } from '../index.ts';

/**
 * Die Seite aus einer Anfrage und einer Umleitung — `/@handle`, `/c/NAME`, `/user/NAME`, `/clip/ID`.
 *
 * Alles hier ist um eine Seite geschrieben, die bei Erfolg einen Frame lang existiert, gelesen wird also
 * nur die *Fehler*-Fassung. `notFollowedDetail` ist der Grund, warum es den Abschnitt gibt: eine kaputte
 * Clip-ID kommt als `200` mit **null Bytes** zurück, und das ist eine Aussage über den Link und nicht
 * über die Instanz.
 *
 * `channelName.*` nimmt `label` als Objekt in den Satz. Die Sätze sind so gebaut, dass die Lücke immer
 * am Ende oder hinter einer kasusneutralen Stelle steht, weil ein `@handle` sich nicht beugen lässt.
 */
export const resolve: Strings['resolve'] = {
  found: 'Gefunden. Wird geöffnet…',
  lookingUp: (label) => `${label} wird gesucht…`,

  subject: {
    channelName: 'Ein Kanal, angegeben über den Namen und nicht über die ID. Er wird auf der Instanz gesucht.',
    clipLabel: 'dieser Clip',
    clip: 'Ein Clip ist ein Abschnitt aus dem Video einer anderen Person. Visious öffnet das Video an der Stelle, wo der Clip anfängt.',
    linkLabel: 'dieser Link',
    link: 'Er wird auf der Instanz gesucht.',
    handle: (name) => `@${name}`,
  },

  notResolvable: {
    title: 'Dieser Link lässt sich nicht auflösen',
    detail: 'Visious weiß nicht, wie es einen Link dieser Art öffnen soll.',
    retryable: false,
  },

  channelName: {
    notFoundTitle: (label) => `Kein Kanal mit dem Namen ${label}`,
    notFoundDetail:
      'YouTube sagt, dort ist nichts. Ein geänderter Handle liest sich genauso wie einer, der nie existiert hat.',
    unreadableTitle: (label) => `Die Antwort für ${label} ließ sich nicht lesen`,
    unreadableDetail: 'Die Instanz hat mit etwas geantwortet, das keinen Kanal benennt.',
    upstreamBrokenTitle: (label) => `${label} ließ sich nicht auflösen`,
    upstreamBrokenDetail:
      'Die Suche ist auf der Instanz fehlgeschlagen, was nicht dasselbe ist wie nichts zu finden. Ein neuer Versuch bekommt dieselbe Antwort.',
  },

  clip: {
    notFound: {
      title: 'Dieser Clip ist weg',
      detail:
        'Ein Clip hängt an seinem Video: wurde das Video gelöscht oder auf privat gestellt, hört der Clip auf zu funktionieren, auch wenn der Link weiterhin gut aussieht.',
      retryable: false,
    },
    unreadableTitle: 'Dieser Clip ließ sich nicht lesen',
    unreadableDetail:
      'Die Instanz hat mit etwas geantwortet, das Visious nicht als Clip lesen konnte.',
  },

  unrecognisedTitle: (label) => `Die Instanz hat ${label} nicht erkannt`,
  unrecognisedDetail: (pageType) =>
    `Sie hat erfolgreich mit einem Seitentyp geantwortet, aus dem sich nichts zum Öffnen machen ließ (${pageType}). Bei einem unbekannten Namen ist das, was YouTube statt eines Fehlers zurückgibt.`,
  unknownPageType: 'eine unbekannte Form',

  notFollowedTitle: (label) => `Die Instanz konnte ${label} nicht folgen`,
  notFollowedDetail:
    'Sie hat mit gar nichts geantwortet, und so weist sie eine ID ab, die sie nicht lesen kann. Prüfe den Link auf eine abgeschnittene oder halb kopierte ID.',
};
